import { useState, useEffect } from "react";
import { API } from "@/App";
import axios from "axios";
import {
  Sprout, Handshake, Star, Crown, TrendingUp,
  ChevronRight, Heart, CheckCircle, MessageCircle, Share2,
} from "lucide-react";

const C = {
  navy: "#2D3561", white: "#FAFAF8", orange: "#F5A623",
  blue: "#4A90D9", gray: "#6B7280", lightgray: "#9CA3AF",
};
const DISPLAY = "'Instrument Serif', Georgia, 'Times New Roman', serif";
const MONO = "'IBM Plex Mono', 'Courier New', monospace";

const LEVEL_ICONS = { "seedling": Sprout, "handshake": Handshake, "star": Star, "crown": Crown };

export default function VolunteerLevel({ compact = false }) {
  const [levelData, setLevelData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const token = localStorage.getItem("ea_token");
    if (!token) return;
    axios.get(`${API}/volunteer/level`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => setLevelData(res.data)).catch(() => {}).finally(() => setLoading(false));
  }, []);

  if (loading || !levelData) {
    return <div className="p-4 animate-pulse" style={{ background: C.white, border: `1px solid ${C.navy}08`, borderRadius: 4 }}><div className="h-16" /></div>;
  }

  const { current_level, next_level, total_points, progress_percent, points_to_next, history } = levelData;
  const LevelIcon = LEVEL_ICONS[current_level.icon] || Star;
  const NextIcon = next_level ? LEVEL_ICONS[next_level.icon] || Star : Crown;

  if (compact) {
    return (
      <div className="p-4" style={{ background: C.white, border: `1px solid ${C.navy}08`, borderRadius: 4 }} data-testid="volunteer-level-compact">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 flex items-center justify-center" style={{ background: current_level.color, borderRadius: 4 }}>
            <LevelIcon className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2">
              <span className="text-sm font-bold" style={{ fontFamily: DISPLAY, color: C.navy }}>{current_level.name}</span>
              <span className="text-[9px] font-semibold px-1.5 py-0.5" style={{ fontFamily: MONO, color: C.navy, background: `${C.navy}08`, borderRadius: 3 }}>
                Nivel {current_level.level}
              </span>
            </div>
            <p className="text-[10px]" style={{ fontFamily: MONO, color: C.lightgray }}>{total_points} puntos</p>
          </div>
        </div>
        {next_level && (
          <div className="mt-3">
            <div className="flex items-center justify-between mb-1">
              <span className="text-[10px]" style={{ fontFamily: MONO, color: C.lightgray }}>{points_to_next} pts para {next_level.name}</span>
              <span className="text-[10px] font-bold" style={{ fontFamily: MONO, color: current_level.color }}>{progress_percent}%</span>
            </div>
            <div className="h-1.5 w-full" style={{ background: `${C.navy}08`, borderRadius: 4 }}>
              <div className="h-full transition-all" style={{ width: `${progress_percent}%`, background: current_level.color, borderRadius: 4 }} />
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="overflow-hidden" style={{ background: C.white, border: `1px solid ${C.navy}08`, borderRadius: 4 }} data-testid="volunteer-level-full">
      {/* Header */}
      <div className="p-6 text-white" style={{ background: current_level.color }}>
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div className="w-14 h-14 flex items-center justify-center" style={{ background: "rgba(255,255,255,0.15)", borderRadius: 4 }}>
              <LevelIcon className="w-7 h-7 text-white" />
            </div>
            <div>
              <p className="text-[9px] uppercase tracking-[0.15em] text-white/50" style={{ fontFamily: MONO }}>Tu nivel</p>
              <h3 className="text-xl font-bold" style={{ fontFamily: DISPLAY }}>{current_level.name}</h3>
              <p className="text-xs text-white/70" style={{ fontFamily: MONO }}>{total_points} puntos totales</p>
            </div>
          </div>
          <div className="w-12 h-12 flex items-center justify-center" style={{ background: "rgba(255,255,255,0.15)", borderRadius: 4 }}>
            <span className="text-xl font-bold" style={{ fontFamily: DISPLAY }}>{current_level.level}</span>
          </div>
        </div>
      </div>

      {/* Progress */}
      {next_level && (
        <div className="px-6 py-4" style={{ borderBottom: `1px solid ${C.navy}08` }}>
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <TrendingUp className="w-3.5 h-3.5" style={{ color: C.lightgray }} />
              <span className="text-xs" style={{ color: C.gray }}>Progreso a</span>
              <span className="text-xs font-bold" style={{ fontFamily: DISPLAY, color: next_level.color }}>{next_level.name}</span>
            </div>
            <span className="text-xs font-bold" style={{ fontFamily: MONO, color: current_level.color }}>{progress_percent}%</span>
          </div>
          <div className="h-2 w-full" style={{ background: `${C.navy}06`, borderRadius: 4 }}>
            <div className="h-full transition-all" style={{ width: `${progress_percent}%`, background: current_level.color, borderRadius: 4 }} />
          </div>
          <p className="text-[10px] text-center mt-2" style={{ fontFamily: MONO, color: C.lightgray }}>
            Te faltan <span className="font-bold" style={{ color: C.navy }}>{points_to_next} puntos</span> para subir de nivel
          </p>
        </div>
      )}

      {/* How to earn */}
      <div className="p-6" style={{ borderBottom: `1px solid ${C.navy}08` }}>
        <h4 className="text-[10px] font-bold tracking-[0.15em] uppercase mb-3" style={{ fontFamily: MONO, color: C.navy }}>
          Como ganar puntos
        </h4>
        <div className="grid grid-cols-2 gap-2">
          {[
            { Icon: Heart, label: "Ofrecer ayuda", points: "+10" },
            { Icon: CheckCircle, label: "Ayuda aceptada", points: "+25" },
            { Icon: MessageCircle, label: "Enviar mensaje", points: "+2" },
            { Icon: Share2, label: "Compartir en redes", points: "+15" },
          ].map((a) => (
            <div key={a.label} className="flex items-center gap-2 px-3 py-2" style={{ background: `${C.navy}04`, borderRadius: 4 }}>
              <a.Icon className="w-3.5 h-3.5 flex-shrink-0" style={{ color: C.blue }} />
              <div className="flex-1 min-w-0">
                <p className="text-[11px] truncate" style={{ color: C.gray }}>{a.label}</p>
              </div>
              <span className="text-[10px] font-bold flex-shrink-0" style={{ fontFamily: MONO, color: C.orange }}>{a.points}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Recent activity */}
      {history && history.length > 0 && (
        <div className="px-6 py-4" style={{ borderBottom: `1px solid ${C.navy}08` }}>
          <h4 className="text-[10px] font-bold tracking-[0.15em] uppercase mb-3" style={{ fontFamily: MONO, color: C.navy }}>Actividad reciente</h4>
          <div className="space-y-0">
            {history.slice(0, 5).map((item, i) => (
              <div key={i} className="flex items-center justify-between py-2" style={{ borderBottom: `1px solid ${C.navy}06` }}>
                <span className="text-xs" style={{ color: C.gray }}>{getActionLabel(item.action)}</span>
                <span className="text-xs font-bold" style={{ fontFamily: MONO, color: C.orange }}>+{item.points}</span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* All levels */}
      <div className="px-6 py-4">
        <h4 className="text-[10px] font-bold tracking-[0.15em] uppercase mb-3" style={{ fontFamily: MONO, color: C.navy }}>Todos los niveles</h4>
        <div className="flex items-center justify-between">
          {levelData.all_levels.map((level, i) => {
            const Icon = LEVEL_ICONS[level.icon] || Star;
            const isActive = level.level === current_level.level;
            const isPast = level.level < current_level.level;
            return (
              <div key={level.level} className="flex items-center">
                <div
                  className="w-9 h-9 flex items-center justify-center transition-all"
                  style={{
                    background: isActive ? `${level.color}15` : isPast ? `${level.color}08` : `${C.navy}04`,
                    borderRadius: 4,
                    border: isActive ? `2px solid ${level.color}` : "none",
                    opacity: isActive ? 1 : isPast ? 0.7 : 0.3,
                    transform: isActive ? "scale(1.1)" : "none",
                  }}
                >
                  <Icon className="w-4 h-4" style={{ color: isActive || isPast ? level.color : C.lightgray }} />
                </div>
                {i < levelData.all_levels.length - 1 && (
                  <ChevronRight className="w-3 h-3 mx-0.5" style={{ color: isPast ? C.lightgray : "#ddd" }} />
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function getActionLabel(action) {
  const labels = {
    "offer_sent": "Ofreciste ayuda",
    "offer_accepted": "Tu ayuda fue aceptada",
    "message_sent": "Enviaste un mensaje",
    "share_social": "Compartiste en redes",
    "first_of_month": "Primera ayuda del mes",
  };
  return labels[action] || action;
}
